import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrderRegion {
  readonly name: string;
  readonly code: string;
}

export interface OrderProduct {
  readonly name: string;
  readonly brand: string;
  readonly sku: string;
  readonly image_url: string | null;
}

export interface OrderItem {
  readonly id: string;
  readonly order_id: string;
  readonly product_id: string;
  readonly quantity: number;
  readonly unit_price: number;
  readonly line_total: number;
  readonly product: OrderProduct;
}

export interface OrderPayment {
  readonly id: string;
  readonly order_id: string;
  readonly method: string;
  readonly amount: number;
  readonly status: string;
  readonly reference: string | null;
  readonly created_at: string;
}

export interface OrderDriver {
  readonly full_name: string;
  readonly phone: string;
  readonly vehicle_type: string;
}

export interface OrderDeliveryMethod {
  readonly name: string;
  readonly type: string;
}

export interface OrderDelivery {
  readonly id: string;
  readonly status: string;
  readonly estimated_at: string | null;
  readonly delivered_at: string | null;
  readonly driver: OrderDriver | null;
  readonly method: OrderDeliveryMethod | null;
}

export interface Order {
  readonly id: string;
  readonly public_ref: string;
  readonly status: string;
  readonly customer_name: string;
  readonly customer_phone: string;
  readonly customer_email: string | null;
  readonly delivery_address: string;
  readonly subtotal_ht: number;
  readonly tax_amount: number;
  readonly delivery_fee: number;
  readonly total_ttc: number;
  readonly payment_method: string;
  readonly notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly region: OrderRegion | null;
}

export interface OrderDetailResponse {
  readonly order: Order;
  readonly items: readonly OrderItem[];
  readonly payments: readonly OrderPayment[];
  readonly delivery: OrderDelivery | null;
}

export interface OrderNote {
  readonly id: string;
  readonly order_id: string;
  readonly admin_user_id: string;
  readonly text: string;
  readonly created_at: string;
  readonly admin_user: { readonly full_name: string } | null;
}

export const ORDER_STATUSES = [
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "cancelled_timeout",
  "payment_failed",
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Statuses that admins can transition TO via PATCH. Terminal states are excluded. */
export type OrderTransitionTarget = Exclude<
  OrderStatus,
  "cancelled_timeout"
>;

/** Bulk status values the backend accepts (see bulk-status route Zod schema). */
export const BULK_STATUS_TARGETS = [
  "confirmed",
  "shipped",
  "delivered",
  "cancelled",
] as const satisfies readonly OrderStatus[];
export type BulkStatusTarget = (typeof BULK_STATUS_TARGETS)[number];

/**
 * i18n keys for order statuses. Consumers should call t(key) with fallback.
 * Keys map to admin.json `orders.status.<status>`.
 */
export const ORDER_STATUS_I18N_KEYS: Readonly<Record<OrderStatus, string>> = {
  pending: "orders.status.pending",
  confirmed: "orders.status.confirmed",
  processing: "orders.status.processing",
  shipped: "orders.status.shipped",
  delivered: "orders.status.delivered",
  cancelled: "orders.status.cancelled",
  cancelled_timeout: "orders.status.cancelled_timeout",
  payment_failed: "orders.status.payment_failed",
};

/** Tailwind color classes per order status. Single source for StatusBadge + anywhere else. */
export const ORDER_STATUS_COLORS: Readonly<Record<OrderStatus, string>> = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  processing: "bg-indigo-100 text-indigo-800 border-indigo-200",
  shipped: "bg-purple-100 text-purple-800 border-purple-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  cancelled_timeout: "bg-rose-100 text-rose-800 border-rose-200",
  payment_failed: "bg-orange-100 text-orange-800 border-orange-200",
};

/** Payment methods accepted in the orders table (DB values). */
export const PAYMENT_METHODS = [
  "wave",
  "orange_money",
  "carte",
  "especes",
  "virement",
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_I18N_KEYS: Readonly<Record<PaymentMethod, string>> = {
  wave: "orders.payment_method.wave",
  orange_money: "orders.payment_method.orange_money",
  carte: "orders.payment_method.carte",
  especes: "orders.payment_method.especes",
  virement: "orders.payment_method.virement",
};

/**
 * A single event row from /api/admin/orders/[id]/timeline.
 *
 * Fed by the status-change trigger (migration 122) and, in future, by
 * payment + shipment webhook consumers. Free-form `kind` intentional
 * during the initial rollout; a CHECK constraint will tighten it once
 * the event set stabilises.
 */
export interface OrderTimelineEvent {
  readonly id: string;
  readonly kind: string;
  readonly summary: string;
  readonly metadata: Readonly<Record<string, unknown>> | null;
  readonly source: string;
  readonly actor_id: string | null;
  readonly created_at: string;
}

export interface OrderTimelineResponse {
  readonly events: readonly OrderTimelineEvent[];
  /**
   * Set when the backing table is not yet migrated (42P01). Callers
   * should render an empty timeline + a small "migration pending"
   * hint rather than treat this as an error.
   */
  readonly warning?: {
    readonly code: string;
    readonly message: string;
  };
}

/** Sortable columns for the list endpoint. Mirrored on backend whitelist. */
export const ORDER_SORT_COLUMNS = [
  "created_at",
  "total_ttc",
  "reference",
  "customer_name",
] as const;
export type OrderSortColumn = (typeof ORDER_SORT_COLUMNS)[number];
export type SortDirection = "asc" | "desc";

// ---------------------------------------------------------------------------
// Payment helpers
// ---------------------------------------------------------------------------

/**
 * Payment statuses that indicate money has actually been captured on our side.
 * Used to gate refund eligibility.
 */
export const PAID_PAYMENT_STATUSES: ReadonlySet<string> = new Set([
  "completed",
  "captured",
  "paid",
  "succeeded",
]);

/**
 * Return the first captured payment on an order, or null if none exist.
 * A captured payment is required before a refund can be initiated.
 */
export function findCapturedPayment(
  payments: readonly OrderPayment[],
): OrderPayment | null {
  return payments.find((p) => PAID_PAYMENT_STATUSES.has(p.status)) ?? null;
}

/** Lightweight row returned by the list endpoint. */
export interface OrderSummary {
  readonly id: string;
  readonly reference: string;
  readonly customer_name: string;
  readonly customer_phone: string;
  readonly total_ttc: number;
  readonly status: string;
  readonly payment_method: string;
  readonly region_id: string | null;
  readonly region: { readonly name: string } | null;
  readonly created_at: string;
}

export interface OrdersListResponse {
  readonly orders: readonly OrderSummary[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface OrdersListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly status?: string | null;
  readonly region_id?: string | null;
  readonly payment_method?: string | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
  readonly q?: string | null;
  readonly sort?: OrderSortColumn | null;
  readonly direction?: SortDirection | null;
}

export interface BulkStatusPayload {
  readonly order_ids: readonly string[];
  readonly status: string;
}

export interface BulkStatusFailure {
  readonly id: string;
  readonly reason: string;
}

export interface BulkStatusResponse {
  readonly updated: number;
  readonly failed: readonly BulkStatusFailure[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(
    entries.map(([k, v]) => [k, String(v)]),
  );
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const ordersApi = {
  /** Fetch paginated order list with optional filters and sorting. */
  list: (params: OrdersListParams = {}) => {
    const qs = toQueryString({
      page: params.page,
      limit: params.limit,
      status: params.status,
      region_id: params.region_id,
      payment_method: params.payment_method,
      date_from: params.date_from,
      date_to: params.date_to,
      q: params.q,
      sort: params.sort,
      direction: params.direction,
    });
    return adminFetch<OrdersListResponse>(`/api/admin/orders${qs}`);
  },

  /** Bulk update order status (confirm / ship / deliver / cancel). */
  bulkUpdateStatus: (
    orderIds: readonly string[],
    newStatus: string,
  ): Promise<BulkStatusResponse> =>
    adminFetch<BulkStatusResponse>("/api/admin/orders/bulk-status", {
      method: "POST",
      body: JSON.stringify({ orderIds, newStatus }),
    }),

  /** GET a single order with items, payments, and delivery details. */
  get: (id: string) =>
    adminFetch<OrderDetailResponse>(`/api/admin/orders/${encodeURIComponent(id)}`),

  /** PATCH order status. */
  updateStatus: (id: string, status: OrderStatus) =>
    adminFetch<Order>(`/api/admin/orders/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  /** POST a note on an order. */
  addNote: (id: string, text: string) =>
    adminFetch<OrderNote>(`/api/admin/orders/${encodeURIComponent(id)}/notes`, {
      method: "POST",
      body: JSON.stringify({ text }),
    }),

  /** GET all notes for an order. */
  getNotes: (id: string) =>
    adminFetch<readonly OrderNote[]>(
      `/api/admin/orders/${encodeURIComponent(id)}/notes`
    ),

  /**
   * GET the append-only event timeline for an order. Sourced from the
   * `order_timeline` table (migration 122). Falls back gracefully to
   * { events: [], warning } when the table has not been applied yet.
   */
  getTimeline: (id: string) =>
    adminFetch<OrderTimelineResponse>(
      `/api/admin/orders/${encodeURIComponent(id)}/timeline`,
    ),

  /**
   * GET the invoice PDF for an order as a Blob.
   *
   * `window.open` cannot attach Authorization headers, so callers should use
   * this helper and open a blob URL instead. Backend requires admin auth.
   */
  getInvoicePdf: (id: string) =>
    adminFetch<Blob>(
      `/api/admin/reports/invoices/${encodeURIComponent(id)}/pdf`,
      { responseType: "blob" },
    ),
} as const;
